import React, { useState, useEffect, useCallback } from "react";
import { ItemList } from "./ItemList";
import { SlidingPanelSystem, usePanelNavigation } from "./vmSlidingPanel";
import { lambdaURL } from "../config";
import { 
  VMProposal,
  VMAssembly,
  extractUserIdFromPK
} from "./vmComponent";
import { VMVotingButtons } from "./vmVoting";
import { extractSuffixFromField, extractTopicIdFromGSI1SK,  extractDateFromGSI1SK, sendRequest } from "./vmUtils";
import { VMDelegation } from "./vmDelegation";
import { VMAssemblyMembers } from "./vmAssemblyMembers";
import { VMProfileQuestionnaire } from "./vmProfileQuestionnaire";
import { VMPublicProfile } from "./vmPublicProfile";
import { VMAdminPanel } from './vmAdminPanel';

//import { VMProfileCompletionGate } from './vmProfileCompletionGate';
import { VMTopicSelector } from './vmTopicSelector';
import { useAuth } from './vmAuthenticate';
//import { VMProvider } from './contexts/VMContext';
//import { AssemblyListPanel } from "./components/Assembly/AssemblyListPanel";
//import { VMAssemblyInvites, BatchInviteInput } from './vmAssemblyInvites';
import "../votemachine.scss";

//import panel_slide from '../sounds/receiver_up.mp3';
//import panel_slide_reverse from '../sounds/receiver_down.mp3';

//FIX: move these to their own music.js (or whatever) file and add more
import note_C from '../sounds/note_C_gain.mp3';
import note_Ef from '../sounds/note_Ef_gain.mp3';
import note_F from '../sounds/note_F_gain.mp3';
import note_G from '../sounds/note_G_gain.mp3';
import note_Bf from '../sounds/note_Bf_gain.mp3';

const VMHome = () => {
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [assemblyProposals, setAssemblyProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortDescending, setSortDescending] = useState(true);
  const [limit, setLimit] = useState(50);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const note_C_sound = new Audio(note_C);
  const note_Ef_sound = new Audio(note_Ef);
  const note_F_sound = new Audio(note_F);
  const note_G_sound = new Audio(note_G);
  const note_Bf_sound = new Audio(note_Bf);

  const { user } = useAuth();//isLoading?

  if (isLoading || !user) {
    return <div>Loading...</div>;
  }

  let userId = null;
  let email = null;
  if (user) {
    userId = user.userId;
    email = user.email;
  }

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);  // increment to trigger useEffect dependencies
  };


  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////

  const AddArgumentButton = ({ type, onClick }) => {
    // Define colors for each argument type
    const getColor = () => {
      switch (type) {
        case 'pro':
          return '#2ecc71';  // Green
        case 'con':
          return '#e74c3c';  // Red
        case 'comment':
          return '#95a5a6';  // Gray
        default:
          return '#95a5a6';
      }
    };

    return (
      <button
        className="vm-create-object-button"
        style={{
          color: getColor()
        }}
        onClick={onClick}
        title={`Add ${type}`}
      >
        { type === 'comment' && (
          'Add Comment'
        )}
        { type !== 'comment' && (
          'Add Argument'
        )}
      </button>
    );
  };


  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  // Proposal panel component
  const ProposalPanel = ({ proposal, onNavigate, user_uuid, panelIndex }) => {
    const { registerRefreshCallback } = usePanelNavigation();
    const [vote, setVote] = useState(null);
    const [showProArgs, setShowProArgs] = useState(true);
    const [showConArgs, setShowConArgs] = useState(true);
    const [showComments, setShowComments] = useState(true);
    const [args, setArgs] = useState({ pro: [], con: [], comments: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    //const [refreshTrigger, setRefreshTrigger] = useState(0);
  
    const refreshArguments = useCallback(() => {
      // Your existing fetch arguments logic
      fetchArguments();
    }, [proposal]);

    useEffect(() => {
      // Register this panel's refresh callback
      registerRefreshCallback(panelIndex, refreshArguments);
    }, [panelIndex, registerRefreshCallback, refreshArguments]);

    const fetchExistingVote = useCallback(async () => {
      try {
        let jsonData = {
          command: "getUserVoteForTarget",
          targetId: extractSuffixFromField(proposal.SK, 'PROPOSAL'),
          targetType: "PROPOSAL"
        };
        console.log("Getting user vote for proposal", jsonData);

        sendRequest(lambdaURL, jsonData)
        .then(data => {
          setVote(null);
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setVote(parsedBody.value);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching vote:', error);
        setVote(null);
      }
    }, [user_uuid, proposal]);

    const fetchArguments = useCallback(async () => {
      try {
        let jsonData = {
          command: "getArgumentsForProposal",
          proposalId: extractSuffixFromField(proposal.SK, 'PROPOSAL')
        };
        console.log("Getting arguments for proposal", jsonData);
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setArgs(parsedBody);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching arguments:', error);
      }
    }, [proposal]);

    // Fetch arguments when component mounts
    useEffect(() => {
      setVote(null);
      fetchExistingVote();
      fetchArguments();
    }, [proposal, user_uuid]);

    const handleVoteClick = async (value) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        // Play sound first for immediate feedback
        let sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
        let sound = sounds[value + 2];
        sound.currentTime = 0;
        sound.play().catch(error => {
          console.log('Sound playback failed:', error);
        });

        let jsonData = {
          command: "createVote",
          targetId: extractSuffixFromField(proposal.SK, 'PROPOSAL'),
          targetType: "PROPOSAL",
          value: value
        };

        console.log("sending createVote request", jsonData);
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            setVote(value);
            setIsSubmitting(false);
          }
        });
      } catch (error) {
        console.error('Error submitting vote:', error);
        setIsSubmitting(false);
      }
    };

    const handleArgumentCreate = (type) => {
      console.log("Creating argument of type: " + type);
      onNavigate(
      <ArgumentEditPanel
        type={type}
        targetId={extractSuffixFromField(proposal.SK, 'PROPOSAL')}
        targetType="PROPOSAL"
        onSuccess={() => fetchArguments()}
        onNavigate={onNavigate}
      />
      );
    };

    const handleEditArgument = (arg) => {
      onNavigate(
        <ArgumentEditPanel
          type={arg.type}
          targetId={extractSuffixFromField(proposal.SK, 'PROPOSAL')}
          targetType="PROPOSAL"
          arg={arg}  // Passing the existing argument makes it an edit operation
          onNavigate={onNavigate}
        />
      );
    };

    const getButtonClass = (buttonValue, currentVote) => {
      if (buttonValue === currentVote) {
        if (buttonValue > 0) return 'vm-vote-button-up-' + buttonValue;
        if (buttonValue < 0) return 'vm-vote-button-down-' + Math.abs(buttonValue);
        return 'vm-vote-button-zero';
      }
      return 'vm-vote-button-default';
    };

    const handleArgumentClick = (arg) => {      
      onNavigate(
        <ArgumentPanel
          arg={arg}
          proposalVote={vote}
        />
      );
    };

    return (
      <div className="vm-section">
        {/* Proposal Title and Description */}
        <div className="vm-section-title">
          <h3>{proposal.title}</h3>
          {proposal.topicName && (
            <div className="text-sm text-gray-600 mb-2">
              <p><i>{proposal.topicName}</i></p>
            </div>
          )}
          <div className="vm-form-text">
            {proposal.description}
          </div>
        </div>
        <p></p>
        <VMVotingButtons
          currentVote={vote}
          onVote={handleVoteClick}
          isSubmitting={isSubmitting}
          size={'normal'}
          playSound={(value) => {
            const sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
            const sound = sounds[value + 2];
            sound.currentTime = 0;
            return sound.play();
          }}
        />
  
        <div className="vm-argument-column relative">
          <div className="vm-argument-header">
            <div className="vm-argument-title vm-argument-for">
              In Favor
              {vote != undefined && vote > 0 && (
                <AddArgumentButton type="pro" onClick={() => handleArgumentCreate('pro')} />
              )}
            </div>
          </div>
          
          {showProArgs && (
            <div className="vm-argument-list">
              <ItemList
                items={args.pro}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No arguments in favor"
                renderItem={(arg) => renderArgumentItem(arg, user_uuid, handleEditArgument)}
              />
            </div>
          )}
        </div>

        <div className="vm-argument-container">
          <div className="vm-argument-column relative">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-against">
                Against
                {vote != undefined && vote < 0 && (
                  <AddArgumentButton type="con" onClick={() => handleArgumentCreate('con')} />
                )}
              </div>
            </div>
            
            {showConArgs && (
              <div className="vm-argument-list">
                <ItemList
                  items={args.con}
                  onItemClick={handleArgumentClick}
                  titleField="title"
                  descriptionField="text"
                  idField="uuid"
                  emptyMessage="No arguments against"
                  renderItem={(arg) => renderArgumentItem(arg, user_uuid, handleEditArgument)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="vm-comments-container">
          <div className="vm-argument-header">
            <div className="vm-argument-title vm-argument-neutral">
              Comments
              <AddArgumentButton type="comment" onClick={() => handleArgumentCreate('comment')} />
            </div>
          </div>
          {showComments && (
            <div className="vm-argument-list">
              <ItemList
                items={args.comments}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No comments yet"
                renderItem={(arg) => renderArgumentItem(arg, user_uuid, handleEditArgument)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  /*
              <div className="vm-item-title">{arg.title}
                {arg.voteTotal > 0 && '+'}{arg.voteTotal}
                <span className="text-gray-400 text-xs ml-1">
                  ({arg.voteCount} votes)
                </span>
              </div>
    */
  const renderArgumentItem = (arg, user_uuid, onManageClick) => {
    const argUserId = arg.PK?.replace("USER#", "");
    const isOwner = (argUserId === user_uuid);
    return (
      <div className="vm-item-container">
        <div className="vm-item-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{arg.title}</span>
            <span style={{ marginRight: '20px' }}>
              {arg.voteTotal > 0 && '+'}{arg.voteTotal}
              <span className="text-gray-400 text-xs ml-1">
                &nbsp;&nbsp;({arg.voteCount} votes)
              </span>
            </span>
          </div>
          {arg.text && (
            <div className="vm-item-description">{arg.text}</div>
          )}
          <div className="text-sm text-gray-600" style={{ fontSize: '12px'}}>
            Author: {argUserId}
          </div>
        </div>
        {isOwner && (
          <button
            className="vm-base-shadow-button-14"
            onClick={(e) => {
              e.stopPropagation();
              onManageClick(arg);
            }}
          >
            Manage
          </button>
        )}
      </div>
    );
  };


//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////

  const ArgumentPanel = ({ arg, proposalVote, parentArgVote , onNavigate }) => {
    const { goBack } = usePanelNavigation();
    const [vote, setVote] = useState(null);
    const [args, setArgs] = useState({ pro: [], con: [], comments: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [sources, setSources] = useState([]);
    const { user } = useAuth();
    const userId = user?.userId;

    const handleRefresh = useCallback(() => {
      setRefreshTrigger(prev => {
        console.log("Setting new refreshTrigger value:", prev + 1);
        return prev + 1;
      });
    }, [refreshTrigger]);

    // Fetch child arguments
    useEffect(() => {

      fetchExistingVote();
      fetchArguments();
      fetchSources();

    }, [arg,refreshTrigger]);

    const fetchArguments = async () => {
      try {
        let jsonData = {
          command: "getArgumentsForProposal", // We'll reuse this endpoint but pass ARGUMENT as targetType
          proposalId: extractSuffixFromField(arg.SK, 'ARGUMENT'), // Using argument ID instead
          targetType: "ARGUMENT"
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setArgs(parsedBody);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching arguments:', error);
      }
    };

    const fetchExistingVote = async () => {
      try {
        let jsonData = {
          command: "getUserVoteForTarget",
          targetId: extractSuffixFromField(arg.SK, 'ARGUMENT'),
          targetType: "ARGUMENT"
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          setVote(null);
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setVote(parsedBody.value);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching vote:', error);
        setVote(null);
      }
    };

    const fetchSources = async () => {
      try {
        const command = {
          command: "getSourcesForArgument",
          argumentId: extractSuffixFromField(arg.SK, 'ARGUMENT')
        };
        sendRequest(lambdaURL, command)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setSources(parsedBody);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching sources:', error);
      }
    };

    const handleArgumentCreate = (type) => {
      onNavigate(
        <ArgumentEditPanel
          type={type}
          targetId={extractSuffixFromField(arg.SK, 'ARGUMENT')}
          targetType="ARGUMENT"
          onSuccess={() => setRefreshTrigger(prev => prev + 1)}
          onNavigate={onNavigate}
        />
      );
    };

    const handleEditArgument = (arg) => {
      onNavigate(
        <ArgumentEditPanel
          type={arg.type}
          targetId={extractSuffixFromField(arg.SK, 'ARGUMENT')}
          targetType="ARGUMENT"
          arg={arg}  // Passing the existing argument makes it an edit operation
          onSuccess={() => setRefreshTrigger(prev => prev + 1)}
          onNavigate={onNavigate}
        />
      );
    };

    const handleArgumentClick = (childArg) => {
      onNavigate(
        <ArgumentPanel
          arg={childArg}
          proposalVote={proposalVote}
          parentArgVote={vote}  // Pass the current argument's vote as parent
          refreshTrigger={refreshTrigger}
        />
      );
    };

    /*
    const canVoteOnArgument = () => {
      if (proposalVote === null) return false;

      // If user voted positively on proposal, they can vote on 'pro' arguments
      if (proposalVote > 0 && arg.type === 'pro') return true;

      // If user voted negatively on proposal, they can vote on 'con' arguments
      if (proposalVote < 0 && arg.type === 'con') return true;

      // Anyone who voted (any value) can vote on comments
      if (arg.type === 'comment') return true;

      return false;
    };*/

    const canVoteOnArgument = () => {
      // If this is a first-level argument (responding to proposal)
      if (arg.GSI1PK.startsWith('PROPOSAL#')) {
        if (proposalVote === null) return false;

        // If user voted positively on proposal, they can vote on 'pro' arguments
        if (proposalVote > 0 && arg.type === 'pro') return true;

        // If user voted negatively on proposal, they can vote on 'con' arguments
        if (proposalVote < 0 && arg.type === 'con') return true;

        // Anyone who voted (any value) can vote on comments
        if (arg.type === 'comment') return true;

        return false;
      }

      // For nested arguments (responding to another argument)
      // Allow voting if there's a vote on the parent argument
      const parentVote = parentArgVote !== undefined ? parentArgVote : vote;
      if (parentVote !== null) {
        // Similar rules as above but based on parent argument vote
        if (parentVote > 0 && arg.type === 'pro') return true;
        if (parentVote < 0 && arg.type === 'con') return true;
        if (arg.type === 'comment') return true;
      }

      return false;
    };

    const handleVoteClick = async (value) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        // Play sound first for immediate feedback
        let sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
        let sound = sounds[value + 2];
        sound.currentTime = 0;
        sound.play().catch(error => {
          console.log('Sound playback failed:', error);
        });

        let jsonData = {
          command: "createVote",
          targetId: extractSuffixFromField(arg.SK, 'ARGUMENT'),
          targetType: "ARGUMENT",
          value: value
        };
        console.log("sending createVote request", jsonData);
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            setIsSubmitting(false);
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setVote(value);
            }
          }
        });
      } catch (error) {
        console.error('Error submitting vote:', error);
        setIsSubmitting(false);
      }
    };

    return (
      <div className="vm-section">
        <div className="vm-form-vertical">
          <div className="vm-section-title">
            <b>{arg.title}</b>
            <div className="vm-form-text">
              {arg.text}
            </div>
            {sources.length > 0 && (
              <div className="mt-4">
                <div className="font-medium mb-2">Sources:</div>
                <div className="space-y-2">
                  {sources.map((source) => (
                    <div key={source.SK}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {source.title}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-sm text-gray-600">
              Author: {extractUserIdFromPK(arg.PK)}
            </div>
          </div>

          {canVoteOnArgument() && (
            <VMVotingButtons
              currentVote={vote}
              onVote={handleVoteClick}
              isSubmitting={isSubmitting}
              size={'normal'}
              disabled={false}
              playSound={(value) => {
                const sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
                const sound = sounds[value + 2];
                sound.currentTime = 0;
                return sound.play();
              }}
            />
          )}

          <div className="vm-argument-column relative">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-for">
                In Favor
                {vote != undefined && vote > 0 && (
                  <AddArgumentButton type="pro" onClick={() => handleArgumentCreate('pro')} />
                )}
              </div>
            </div>

            <div className="vm-argument-list">
              <ItemList
                items={args.pro}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No arguments in favor"
                renderItem={(arg) => renderArgumentItem(arg, userId, handleEditArgument)}
              />
            </div>
          </div>

          <div className="vm-argument-container">
            <div className="vm-argument-column relative">
              <div className="vm-argument-header">
                <div className="vm-argument-title vm-argument-against">
                  Against
                  {vote != undefined && vote < 0 && (
                    <AddArgumentButton type="con" onClick={() => handleArgumentCreate('con')} />
                  )}
                </div>
              </div>

              <div className="vm-argument-list">
                <ItemList
                  items={args.con}
                  onItemClick={handleArgumentClick}
                  titleField="title"
                  descriptionField="text"
                  idField="uuid"
                  emptyMessage="No arguments against"
                  renderItem={(arg) => renderArgumentItem(arg, userId, handleEditArgument)}
                />
              </div>
            </div>
          </div>

          <div className="vm-comments-container">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-neutral">
                Comments
                <AddArgumentButton type="comment" onClick={() => handleArgumentCreate('comment')} />
              </div>
            </div>
            <div className="vm-argument-list">
              <ItemList
                items={args.comments}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No comments yet"
                renderItem={(arg) => renderArgumentItem(arg, userId, handleEditArgument)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };


  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  const ArgumentEditPanel = ({
    type,
    targetId,
    targetType = 'PROPOSAL',
    arg = null,
    onSuccess = 0,
    onNavigate
  }) => {
    const { goBack } = usePanelNavigation();
    const [title, setTitle] = useState(arg?.title || '');
    const [description, setDescription] = useState(arg?.text || '');
    const [sources, setSources] = useState([]);
    const isEdit = !!arg;

    useEffect(() => {
      setTitle(arg?.title || '');
      setDescription(arg?.text || '');
      fetchSources();
    }, [arg]);

    // Helper function to get argument type display text
    const getArgumentTypeText = () => {
      switch (type) {
        case 'pro':
          return 'Supporting';
        case 'con':
          return 'Counter';
        case 'comment':
          return 'Comment';
        default:
          return '';
      }
    };

    const fetchSources = async () => {
      if (!isEdit) return;

      try {
        const command = {
          command: "getSourcesForArgument",
          argumentId: extractSuffixFromField(arg.SK, 'ARGUMENT')
        };
        sendRequest(lambdaURL, command)
        .then(data => {
          if (data.body) {
            console.log("Got sources: " + data.body)
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setSources(parsedBody);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching sources:', error);
      }
    };

    const handleSubmit = () => {
      if (!title.trim() || !description.trim()) return;

      let jsonData = {
        command: isEdit ? "updateArgument" : "createArgument",
        targetId: targetId,
        targetType: targetType,
        title: title.trim(),
        text: description.trim(),
        type: type,
        uuid: isEdit ? extractSuffixFromField(arg.SK, 'ARGUMENT') : null
      };

      console.log("sending createArgument request", jsonData);
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log(`${isEdit ? 'Updated' : 'Created'} ${type} successfully`);
          setTitle('');
          setDescription('');
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleDelete = () => {
      if (!isEdit) return;

      let jsonData = {
        command: "deleteArgument",
        uuid: extractSuffixFromField(arg.SK, 'ARGUMENT')
      };
      console.log("sending deleteArgument request", jsonData);
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log(`Deleted ${type} successfully`);
          setTitle('');
          setDescription('');
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleCancel = () => {
      setTitle('');
      setDescription('');
      goBack();
    };

    const handleAddSource = () => {
      onNavigate(
        <VMSourceEditPanel
          argumentId={isEdit ? extractSuffixFromField(arg.SK, 'ARGUMENT') : null}
          onSuccess={() => {
            // Refresh argument data if needed
            if (onSuccess) onSuccess();
          }}
        />
      );
    };

    // Get appropriate placeholder text based on type
    const getPlaceholderText = () => {
      switch (type) {
        case 'pro':
          return 'Provide your supporting argument...';
        case 'con':
          return 'Provide your counter argument...';
        case 'comment':
          return 'Write your comment...';
        default:
          return 'Enter your text...';
      }
    };

    return (
      <div className="vm-section">
        <div className="vm-section-title">
          <h2>{isEdit ? 'Edit' : 'Add'} {getArgumentTypeText()}</h2>
        </div>

        <div className="vm-form-vertical">
          <input
            className="vm-form-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={type === 'comment' ? 'Comment title' : 'Argument title'}
          />

          <textarea
            className="vm-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={getPlaceholderText()}
            rows="6"
          />

          {isEdit && sources.length > 0 && (
            <div className="mb-4">
              <div className="font-medium mb-2">Sources:</div>
              <div className="space-y-2">
                {sources.map((source) => (
                  <div key={source.SK} className="flex items-center gap-2">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {source.title}
                    </a>
                    <button
                      onClick={() => {
                        onNavigate(
                          <VMSourceEditPanel
                            source={source}
                            argumentId={extractSuffixFromField(arg.SK, 'ARGUMENT')}
                            onSuccess={() => fetchSources()}
                          />
                        );
                      }}
                      className="text-med text-gray-500 hover:text-gray-700"
                      style={{ marginLeft: '15px'}}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="vm-proposal-vote-button-container">
            <button
              className="vm-form-button"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save' : 'Add'}
            </button>
            {isEdit && (
              <button
                className="vm-form-button"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
            <button
              className="vm-form-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              className="vm-form-button"
              onClick={handleAddSource}
            >
              Add Source
            </button>
          </div>
        </div>
      </div>
    );
  };



  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  const VMHomePanel = () => {

    const handleRedClick = () => {
      console.log("Red Click!");
    };

    const handleBlueClick = () => {
      console.log("Blue Click!");
    };

    const handleGreenClick = () => {
      console.log("Green Click!");
    };

    const handleYellowClick = () => {
      console.log("Yellow Click!");
    };
      /*onNavigate(
        <VMSourceEditPanel
          argumentId={isEdit ? extractSuffixFromField(arg.SK, 'ARGUMENT') : null}
          onSuccess={() => {
            // Refresh argument data if needed
            if (onSuccess) onSuccess();
          }}
        />
      );*/

    return (
      <div className="vm-section">
        <div className="interface-container">
          {/* Top screen section */}
          <div className="screen-section">
            <div className="main-screen">
              {/* Screen content will go here */}
            </div>
          </div>

          {/* Bottom control section */}
          <div className="controls-section">
            {/* Row of wide buttons */}
            <div className="wide-buttons-row">
              <button className="wide-button">-2</button>
              <button className="wide-button">-1</button>
              <button className="wide-button">0</button>
              <button className="wide-button">+1</button>
              <button className="wide-button">+2</button>
            </div>

            <div style={{ position: 'relative', width: 240, height: 240 }}>
              <button className="quadrant-button quadrant-top" onClick={handleRedClick} />
              <button className="quadrant-button quadrant-right" onClick={handleBlueClick} />
              <button className="quadrant-button quadrant-bottom" onClick={handleYellowClick} />
              <button className="quadrant-button quadrant-left" onClick={handleGreenClick} />
            </div>
          </div>
        </div>
      </div>
    );
  };

/*



              <button className="quadrant-button quadrant-top" style={{ background: '#fb442d' }} />
              <button className="quadrant-button quadrant-right" style={{ background: '#20a0e0' }} />
              <button className="quadrant-button quadrant-bottom" style={{ background: '#eed827' }} />
              <button className="quadrant-button quadrant-left" style={{ background: '#18d870' }} />

     round-vm-button


            <div className="quadrant-button-container">
              <button className="quadrant-button">
                <div className="quadrant top"></div>
                <div className="quadrant right"></div>
                <div className="quadrant bottom"></div>
                <div className="quadrant left"></div>
                <div className="center-circle"></div>
              </button>
            </div>


  const VMHomePanel = () => {

    return (
      <div className="vm-section">
      <div className="machine-interface">
        <div className="top-center-wrapper">
          <button className="main-button">
            <span style={{ fontSize: '64px'}}>🌎</span>
          </button>
        </div>
        <button className="main-button bottom-left">
          <span style={{ fontSize: '64px'}}>🛠️</span>
        </button>
        <button className="main-button bottom-right">
          <span style={{ fontSize: '64px'}}>🌈</span>
        </button>
        <button className="small-button top-right">
          <span style={{ fontSize: '48px'}}>🪪</span>
        </button>
        <button className="small-button top-left">
          <span style={{ fontSize: '36px'}}>📊</span>
        </button>
      </div>
    </div>
    );
  };*/

/*  <div className="vm-quoted-text" >
  <p>"Congress shall make no law ... prohibiting ... the right of the people peaceably to assemble, and to petition the
  Government for a redress of grievances. - First Amendment to the U.S. Constitution"</p>
</div>*/

  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  const VMSourceEditPanel = ({ argumentId, source = null, onSuccess }) => {
    const { goBack } = usePanelNavigation();
    const [title, setTitle] = useState(source?.title || '');
    const [url, setUrl] = useState(source?.url || '');
    const [description, setDescription] = useState(source?.description || '');
    const isEdit = !!source;

    const handleSubmit = () => {
      if (!title.trim() || !url.trim() || !description.trim()) return;

      let jsonData = {
        command: isEdit ? "updateSource" : "createSource",
        argumentId: argumentId,
        title: title.trim(),
        url: url.trim(),
        description: description.trim(),
        uuid: isEdit ? extractSuffixFromField(source.SK, 'SOURCE') : null
      };

      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log(`${isEdit ? 'Updated' : 'Created'} source successfully`);
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleDelete = () => {
      if (!isEdit) return;

      let jsonData = {
        command: "deleteSource",
        uuid: extractSuffixFromField(source.SK, 'SOURCE')
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log("Deleted source successfully");
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    return (
      <div className="vm-section">
        <div className="vm-section-title">
          <h2>{isEdit ? 'Edit' : 'Add'} Source</h2>
        </div>

        <div className="vm-form-vertical">
          <input
            className="vm-form-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Source title"
          />

          <input
            className="vm-form-input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="URL (e.g., https://example.com)"
          />

          <textarea
            className="vm-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Source description..."
            rows="4"
          />

          <div className="vm-proposal-vote-button-container">
            <button
              className="vm-form-button"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save' : 'Add'}
            </button>
            {isEdit && (
              <button
                className="vm-form-button"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
            <button
              className="vm-form-button"
              onClick={goBack}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const VMSourcePanel = ({ source, onNavigate }) => {
    const { goBack } = usePanelNavigation();
    const [vote, setVote] = useState(null);
    const [args, setArgs] = useState({ pro: [], con: [], comments: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useAuth();
    const userId = user?.userId;

    const note_C_sound = new Audio(note_C);
    const note_Ef_sound = new Audio(note_Ef);
    const note_F_sound = new Audio(note_F);
    const note_G_sound = new Audio(note_G);
    const note_Bf_sound = new Audio(note_Bf);

    useEffect(() => {

      fetchExistingVote();
      fetchArguments();
    }, [source, userId]);

    const fetchExistingVote = async () => {
      try {
        let jsonData = {
          command: "getUserVoteForTarget",
          targetId: extractSuffixFromField(source.SK, 'SOURCE'),
          targetType: "SOURCE"
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          setVote(null);
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setVote(parsedBody.value);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching vote:', error);
        setVote(null);
      }
    };

    const fetchArguments = async () => {
      try {
        let jsonData = {
          command: "getArgumentsForSource",
          sourceId: extractSuffixFromField(source.SK, 'SOURCE')
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody) {
              setArgs(parsedBody);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching arguments:', error);
      }
    };

    const handleVoteClick = async (value) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        let sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
        let sound = sounds[value + 2];
        sound.currentTime = 0;
        sound.play().catch(error => {
          console.log('Sound playback failed:', error);
        });

        let jsonData = {
          command: "createVote",
          targetId: extractSuffixFromField(source.SK, 'SOURCE'),
          targetType: "SOURCE",
          value: value
        };

        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            setVote(value);
            setIsSubmitting(false);
          }
        });
      } catch (error) {
        console.error('Error submitting vote:', error);
        setIsSubmitting(false);
      }
    };

    const handleArgumentCreate = (type) => {
      onNavigate(
        <ArgumentEditPanel
          type={type}
          targetId={extractSuffixFromField(source.SK, 'SOURCE')}
          targetType="SOURCE"
          onSuccess={() => fetchArguments()}
        />
      );
    };

    const handleArgumentClick = (arg) => {
      onNavigate(
        <ArgumentPanel
          arg={arg}
          sourceVote={vote}
        />
      );
    };

    return (
      <div className="vm-section">
        <div className="vm-form-vertical">
          <div className="vm-section-title">
            <h2>{source.title}</h2>
            <div className="vm-form-text">
              <a href={source.url} target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline">
                {source.url}
              </a>
            </div>
            <div className="vm-form-text">
              {source.description}
            </div>
            <div className="text-sm text-gray-600">
              Author: {extractUserIdFromPK(source.PK)}
            </div>
          </div>

          <VMVotingButtons
            currentVote={vote}
            onVote={handleVoteClick}
            isSubmitting={isSubmitting}
            size={'normal'}
            playSound={(value) => {
              const sounds = [note_C_sound, note_Ef_sound, note_F_sound, note_G_sound, note_Bf_sound];
              const sound = sounds[value + 2];
              sound.currentTime = 0;
              return sound.play();
            }}
          />

          <div className="vm-argument-column relative">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-for">
                Supporting Arguments
                <AddArgumentButton type="pro" onClick={() => handleArgumentCreate('pro')} />
              </div>
            </div>
            <div className="vm-argument-list">
              <ItemList
                items={args.pro}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No supporting arguments"
                renderItem={(arg) => renderArgumentItem(arg, userId)}
              />
            </div>
          </div>

          <div className="vm-argument-column relative">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-against">
                Counter Arguments
                <AddArgumentButton type="con" onClick={() => handleArgumentCreate('con')} />
              </div>
            </div>
            <div className="vm-argument-list">
              <ItemList
                items={args.con}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No counter arguments"
                renderItem={(arg) => renderArgumentItem(arg, userId)}
              />
            </div>
          </div>

          <div className="vm-comments-container">
            <div className="vm-argument-header">
              <div className="vm-argument-title vm-argument-neutral">
                Comments
                <AddArgumentButton type="comment" onClick={() => handleArgumentCreate('comment')} />
              </div>
            </div>
            <div className="vm-argument-list">
              <ItemList
                items={args.comments}
                onItemClick={handleArgumentClick}
                titleField="title"
                descriptionField="text"
                idField="uuid"
                emptyMessage="No comments yet"
                renderItem={(arg) => renderArgumentItem(arg, userId)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  const TopicEditPanel = ({ topicId, assemblyId, topic, onSuccess, arg = null }) => { //FIX: topicID, topic, arg?? something is not needed
    const { goBack } = usePanelNavigation();
    const [name, setName] = useState(topic?.name || '');
    const [description, setDescription] = useState(topic?.description || '');
    const isEdit = !!arg; // If arg exists, we're editing

    useEffect(() => {
      // If editing, set values from topic, otherwise clear them
      if (topic) {
        setName(topic.name || '');
        setDescription(topic.description || '');
      } else {
        setName('');
        setDescription('');
      }
    }, [topic]); // Only run when topic changes

    const handleSubmit = () => {
      if (!name.trim() || !description.trim()) return;

      let jsonData = {
        command: isEdit ? "updateTopic" : "createTopic",
        topicId: topicId,
        assemblyId: assemblyId,
        name: name.trim(),
        description: description.trim(),
        uuid: isEdit ? extractSuffixFromField(arg.SK, 'TOPIC') : null
      };

      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data) {
          console.log(`${isEdit ? 'Updated' : 'Created'} topic successfully`);
          goBack();
          if (onSuccess) { onSuccess(); }
        }
      });
    };

    const handleDelete = () => {
      if (!isEdit) return;

      let jsonData = {
        command: "deleteTopic",
        uuid: extractSuffixFromField(arg.SK, 'TOPIC')
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log("Deleted topic successfully");
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    return (
      <div className="vm-section">
        <div className="vm-section-title">
          <h2>{isEdit ? 'Edit' : 'Add'} Topic</h2>
        </div>

        <div className="vm-form-vertical">
          {!isEdit && (
            <VMTopicSelector
              setName={setName}
              isEdit={isEdit}
            />
          )}
          <input
            className="vm-form-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Topic name"
          />

          <textarea
            className="vm-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your topic..."
            rows="6"
          />

          <div className="vm-proposal-vote-button-container">
            <button
              className="vm-form-button"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save' : 'Add'}
            </button>
            {isEdit && (
              <button
                className="vm-form-button"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
            <button
              className="vm-form-button"
              onClick={goBack}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };


  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////


  // Source panel component
  const SourcePanel = ({ source }) => {
    return (
      <div className="vm-section">
        <div className="vm-section-title">{source.title}</div>
        <div className="vm-form-vertical">
          <div className="vm-form-text">
            <b>URL:</b> <a href={source.url} target="_blank" rel="noopener noreferrer">{source.url}</a>
          </div>
          <div className="vm-form-text">
            <b>Description:</b> {source.description}
          </div>
          <div className="vm-form-text text-sm text-gray-600" style={{ fontSize: '12px'}}>
            Author: {extractUserIdFromPK(source.PK)}
          </div>
        
        </div>
      </div>
    );
  };


  //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////
  // User Home Page Panel

  const AssemblyListPanel = ({ onNavigate }) => {
    const [selectedAssembly, setSelectedAssembly] = useState(null);
    const [assemblyProposals, setAssemblyProposals] = useState([]);
    const [discoverableAssemblies, setDiscoverableAssemblies] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const { user, isLoading } = useAuth();
    const userId = user?.userId;

    let isAdmin = user?.isAdmin || false;

    console.log("!!! AssemblyList userId: " + userId + " user: " + JSON.stringify(user));

    useEffect(() => {
      if (!isLoading && !user) {
        console.log('No user detected in AssemblyListPanel');
        return;
      }
      if (!isLoading && user) {
        console.log('User detected in AssemblyListPanel:', user);
      }
    }, [user, isLoading]);

    const handleRefresh = useCallback(() => {
      setRefreshTrigger(prev => {
        return prev + 1;
      });
    }, [refreshTrigger]);

    // Fetch discoverable assemblies on component mount
    useEffect(() => {
      if (!userId) return;
      console.log("AssemblyListPanel useEffect triggered, userId is:", userId);
      const fetchDiscoverableAssemblies = () => {
        const jsonData = {
          command: "getDiscoverableAssemblies",
          userId: userId
        };
        console.log("Trying to fetch discoverable assemblies: " + JSON.stringify(jsonData));
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody && Array.isArray(parsedBody)) {
              setDiscoverableAssemblies(parsedBody);
            }
          }
        });
      };
  
      fetchDiscoverableAssemblies();
    }, [userId]);
  
    const fetchAssemblyProposals = async (assemblyId) => {
      setIsLoading(true);
      
      const jsonData = {
        command: "getAssemblyProposals",
        //command: "getAllProposals",
        assemblyId: assemblyId,
        limit: limit,
        descending: sortDescending.toString()
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          const parsedBody = JSON.parse(data.body);
          if (parsedBody && Array.isArray(parsedBody)) {
            setAssemblyProposals(parsedBody);
          }
        }
      });
    };

    const handleJoinAssembly = (assemblyId) => {
      const jsonData = {
        command: "requestMembership",
        assemblyId: assemblyId
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          const parsedBody = JSON.parse(data.body);
          if (parsedBody && Array.isArray(parsedBody)) {
            setDiscoverableAssemblies(prev =>
              prev.filter(assembly => extractSuffixFromField(assembly.SK, 'ASSEMBLY') !== assemblyId)
            );
          }
          setRefreshTrigger(prev => {
            return prev + 1;
          });
        }
      });
    };

    const handleCreateAssembly = () => {
      onNavigate(
        <AssemblyEditPanel
          key={Date.now()}
          onSuccess={() => {
            handleRefresh();
          }}
        />
      );
    };

    const handleEditAssembly = (assembly) => {
      onNavigate(
        <AssemblyEditPanel
          assembly={assembly}
          onSuccess={() => {
            handleRefresh();
          }}
        />
      );
    };

    const handleAssemblyClick = (assembly) => {
      onNavigate(
        <ProposalListPanel 
          assembly={assembly}
        />
      );
    };

    const handleProfileClick = () => {
      onNavigate(
        <VMProfileQuestionnaire
          assemblyId={"387a1596-ce28-4079-9bd9-aa87301aae28"}
        />
      );
    };

    const handleReorder = async (reorderedAssemblies) => {
      try {
        let jsonData = {
          command: "updateAssemblyOrder",
          assemblies: reorderedAssemblies
        };

        await sendRequest(lambdaURL, jsonData);
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('Error updating assembly order:', error);
      }
    };

    // Render a single discoverable assembly item
    const renderDiscoverableAssembly = (assembly) => (
      <div className="flex justify-between items-center p-2">
        <div>
          <div className="font-semibold">{assembly.name}</div>
          <div className="text-sm text-gray-600">{assembly.description}</div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleJoinAssembly(extractSuffixFromField(assembly.SK, 'ASSEMBLY'));
          }}
          className="vm-form-button"
        >
          Join
        </button>
      </div>
    );

    return (
      <div className="vm-section">
        <div className="vm-quoted-text">
          "Congress shall make no law ... prohibiting ... the right of the people peaceably to assemble, and to petition the
          Government for a redress of grievances. - First Amendment to the U.S. Constitution"
        </div>

        <div className="vm-section-title">
          <h1>Home</h1>
          <div className="flex gap-4 justify-center">
            <button
              style={{ fontSize: '20px', marginBottom: '20px', padding: '5px 40px' }}
              onClick={handleProfileClick}
            >
              My Profile
            </button>
            { isAdmin && (
              <button
                style={{ fontSize: '20px', marginBottom: '20px', padding: '5px 40px' }}
                onClick={() => onNavigate(<VMAdminPanel />)}
              >
                Admin Panel
              </button>
            )}
          </div>
        </div>

        <div className="mb-8">
          <div className="vm-section-sub-header">
            <b>My Assemblies</b>
            <button
              className="vm-create-object-button"
              onClick={handleCreateAssembly}
              style={{ left: '50%', marginLeft: '20px' }}
            >Create Your Own Assembly</button>
          </div>
          <VMAssembly 
            user_uuid={userId}
            onAssemblyClick={handleAssemblyClick}
            onEditAssembly={handleEditAssembly}
            interactive={true}
            concise={true}
            refreshTrigger={refreshTrigger}
          />
        </div>

        <DiscoverableAssemblies 
          user_uuid={userId}
          onRefresh={handleRefresh} 
          refreshTrigger={refreshTrigger}
          key={refreshTrigger}
        />
      </div>
    );
  };

  const AssemblyEditPanel = ({ assembly = null, user_uuid, onSuccess  }) => {
    const { goBack, pushPanel } = usePanelNavigation();
    const [name, setName] = useState(assembly?.name || '');
    const [description, setDescription] = useState(assembly?.description || '');
    const [isPrivate, setIsPrivate] = useState(assembly?.isPrivate || false);
    const [activeTab, setActiveTab] = useState('details');
    const isEdit = !!assembly;

    const handleSubmit = () => {
      if (!name.trim() || !description.trim()) return;

      let jsonData = {
        command: isEdit ? "updateAssembly" : "createAssembly",
        name: name.trim(),
        description: description.trim(),
        isPrivate: isPrivate,
        uuid: isEdit ? extractSuffixFromField(assembly.SK, 'ASSEMBLY') : null
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log(`${isEdit ? 'Updated' : 'Created'} assembly successfully`);
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleDelete = () => {
      if (!isEdit) return;

      let jsonData = {
        command: "deleteAssembly",
        uuid: extractSuffixFromField(assembly.SK, 'ASSEMBLY')
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log("Deleted assembly successfully");
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleViewMembers = () => {
      pushPanel(
        <VMAssemblyMembers
          assembly={assembly}
          user_uuid={user_uuid}
        />
      );
    };

    return (
      <div className="vm-section">
        <div className="vm-section-title">
          <h2>{isEdit ? 'Edit' : 'Create'} Assembly</h2>
        </div>

        <div className="vm-form-vertical">
          <input
            className="vm-form-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Assembly name"
          />

          <textarea
            className="vm-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Assembly description..."
            rows="6"
          />

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            Private Assembly
          </label>

          <div className="vm-proposal-vote-button-container">
            <button
              className="vm-form-button"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save' : 'Create'}
            </button>
            {isEdit && (
              <>
                <button
                  className="vm-form-button"
                  onClick={handleDelete}
                >
                  Delete
                </button>
                <button
                  className="vm-form-button"
                  onClick={handleViewMembers}
                >
                  Members
                </button>
              </>
            )}
            <button
              className="vm-form-button"
              onClick={goBack}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////


  const DiscoverableAssemblies = ({ user_uuid, onRefresh, refreshTrigger }) => {
    const [assemblies, setAssemblies] = useState([]);
    const [selectedAssembly, setSelectedAssembly] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, isLoading: authLoading } = useAuth();
  
    useEffect(() => {
      // Only fetch if authentication is complete and we have a user
      if (!authLoading && user) {
        console.log("DiscoverableAssemblies useEffect triggered with authenticated user");
        fetchDiscoverableAssemblies();
      } else if (!authLoading && !user) {
        setIsLoading(false);
        setError("Please log in to view discoverable assemblies");
      }
    }, [user, authLoading, refreshTrigger]);
  
    const fetchDiscoverableAssemblies = async () => {
      console.log("fetchDiscoverableAssemblies called");
      setIsLoading(true);
      let jsonData = {
        command: "getDiscoverableAssemblies"
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          const parsedBody = JSON.parse(data.body);
          if (parsedBody && Array.isArray(parsedBody)) {
            setIsLoading(false);
            setAssemblies(parsedBody);
          }
        }
      });
    };
  
    const handleDirectJoin = async () => {
      if (!selectedAssembly) return;
  
      let jsonData = {
        command: "addMembership",
        assemblyId: extractSuffixFromField(selectedAssembly.SK, 'ASSEMBLY'),
        status: "APPROVED"
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          setSelectedAssembly(null);
          fetchDiscoverableAssemblies();
          onRefresh();
          console.log("Refreshed assemblies!!!");
        } else {
          console.log("data body was null!!!");
        }
      });
    };
  
    const handleRequestJoin = async () => {
      if (!selectedAssembly) return;
  
      let jsonData = {
        command: "addMembership",
        assemblyId: extractSuffixFromField(selectedAssembly.SK, 'ASSEMBLY'),
        status: "PENDING"
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          setSelectedAssembly(null);
          fetchDiscoverableAssemblies();
        }
      });
    };
  
    const handleCancel = () => {
      setSelectedAssembly(null);
    };
  
    if (isLoading) {
      return (
        <div className="vm-section">
          <div className="vm-section-title"><h2>Discover Assemblies</h2></div>
          <div className="vm-form-text">Loading assemblies...</div>
        </div>
      );
    }
  
    return (
      <div className="vm-section">
        <div className="vm-section-sub-header"><b>Discover Assemblies</b></div>
        
        <div className="vm-form-vertical">
          <select
            className="vm-form-input"
            value={selectedAssembly ? extractSuffixFromField(selectedAssembly.SK, 'ASSEMBLY') : ''}
            onChange={(e) => {
              const assembly = assemblies.find(a => extractSuffixFromField(a.SK, 'ASSEMBLY') === e.target.value);
              setSelectedAssembly(assembly || null);
            }}
          >
            <option value="">Select an assembly</option>
            {Array.isArray(assemblies) && assemblies.map((assembly) => (
              <option key={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
                    value={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}>
                {assembly.name}
              </option>
            ))}
          </select>
  
          {selectedAssembly && (
            <div className="vm-form-vertical">
              <div className="vm-form-text"><b>{selectedAssembly.name}</b></div>
              <div className="vm-form-text">{selectedAssembly.description}</div>
              <div className="vm-proposal-vote-button-container">
                <button 
                  className="vm-form-button"
                  onClick={selectedAssembly.isInvite ? handleRequestJoin : handleDirectJoin}
                >
                  {selectedAssembly.isInvite ? 'Request to Join' : 'Join'}
                </button>
                <button 
                  className="vm-form-button"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };


//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////


  const ProposalListPanel = ({ assembly, onNavigate, user_uuid }) => {
    const [proposals, setProposals] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sortDescending, setSortDescending] = useState(true);
    const [limit, setLimit] = useState(50);
    const [topicsRefreshTrigger, setTopicsRefreshTrigger] = useState(0);
    const [proposalsRefreshTrigger, setProposalsRefreshTrigger] = useState(0);
    const { user } = useAuth();
    const userId = user?.userId;
  
    useEffect(() => {
      if (!assembly || !extractSuffixFromField(assembly.SK, 'ASSEMBLY')) return;
      fetchAssemblyProposals();
    }, [assembly, sortDescending, limit, selectedTopic, proposalsRefreshTrigger]);

    useEffect(() => {
      const fetchTopics = async () => {
        if (!assembly) return;
        let jsonData = {
          command: "getTopicsForAssembly",
          assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY')
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody && Array.isArray(parsedBody)) {
              const sortedTopics = parsedBody.sort((a, b) => {
                if (a.name === "No Topic") return -1;
                if (b.name === "No Topic") return 1;
                return a.name.localeCompare(b.name);
              });
              setTopics(sortedTopics);
            }
          }
        });
      };
      fetchTopics();
    }, [assembly, topicsRefreshTrigger]);

    const fetchAssemblyProposals = async () => {
      setIsLoading(true);
      
      console.log("Fetching assembly proposals, assembly: " + assembly.name);
      const jsonData = {
        command: "getAssemblyProposals",
        assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY'),
        topicId: selectedTopic,
        limit: limit,
        descending: sortDescending.toString()
      };
      console.log("Sending request to getAssemblyProposals", jsonData);
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          const parsedBody = JSON.parse(data.body);
          if (parsedBody && Array.isArray(parsedBody)) {
            setIsLoading(false);
            setProposals(parsedBody);
          }
        }
      });
    };
  
    const handleProposalClick = (proposal) => {
      onNavigate(
        <ProposalPanel
          key={extractSuffixFromField(proposal.SK, 'PROPOSAL')}
          proposal={proposal}
          user_uuid={userId}
          assemblyName={assembly.name}
        />
      );
    };

    const handleCreateProposal = () => {
      onNavigate(
        <ProposalEditPanel
          key={Date.now()}
          assemblyId={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
          user_uuid={userId}
          onNavigate={onNavigate}
          parentTopicId = {selectedTopic}
          onSuccess={() => setProposalsRefreshTrigger(prev => prev + 1)}
        />
      );
    };

    const handleEditProposal = (proposal) => {
      onNavigate(
        <ProposalEditPanel
          proposal={proposal}
          assemblyId={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
          user_uuid={userId}
          onNavigate={onNavigate}
          onSuccess={() => setProposalsRefreshTrigger(prev => prev + 1)}
        />
      );
    };

    const handleCreateTopic = () => {
      onNavigate(
        <TopicEditPanel
          key={Date.now()}
          assemblyId={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
          user_uuid={userId}
          onSuccess={() => setTopicsRefreshTrigger(prev => prev + 1)}
        />
      );
    };

    const handleViewProfile = (user) => {
      onNavigate(
        <VMPublicProfile
          user={user}
          assemblyId={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
        />
      );
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'No date';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'Invalid date format';
        }
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error formatting date';
      }
    };
  
    const renderProposalItem = (proposal) => (
      <div className="vm-proposal-item">
        <div className="vm-proposal-title">{proposal.title}</div>
        <div
          className="vm-item-description"
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            WebkitLineClamp: 2,
            marginBottom: '10px'
          }}
        >
          {proposal.description}
        </div>
        <div className="vm-proposal-metadata">
          Created: {extractDateFromGSI1SK(proposal.GSI1SK)}<br></br>
          <span className="ml-4"  style={{ fontSize: '12px'}}>Author: {extractUserIdFromPK(proposal.PK)}</span>
        </div>
      </div>
    );

    /*
          <select
            className="vm-form-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={10}>10 proposals</option>
            <option value={25}>25 proposals</option>
            <option value={50}>50 proposals</option>
            <option value={100}>100 proposals</option>
          </select>
          <div className="flex items-center gap-2">
          </div>
    */
  
    //className="vm-form-button-sort"
    return (
      <div className="vm-section">
        <div className="vm-section-sub-header">
          <b>{assembly.name} - Proposals</b>
        </div>
        
        <div className="vm-proposals-controls">
            <select
              className="vm-form-select"
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
            >
              <option value="">All Topics</option>
              {topics.map(topic => (
                <option
                  key={extractSuffixFromField(topic.SK, 'TOPIC')}
                  value={extractSuffixFromField(topic.SK, 'TOPIC')}
                >
                  {topic.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleCreateTopic()}
            >
              +
            </button>
          <button
            onClick={() => setSortDescending(!sortDescending)}
          >
            Sort: {sortDescending ? 'Newest' : 'Oldest'}
          </button>
          <button
            className="vm-create-object-button"
            onClick={handleCreateProposal}
            style={{ marginLeft: 'auto' }}
          >Submit Your Own Proposal</button>
        </div>
  
        {isLoading ? (
          <div className="vm-loading">Loading proposals...</div>
        ) : (
          <>
            <VMProposal
              user_uuid={user_uuid}
              onProposalClick={handleProposalClick}
              onEditProposal={handleEditProposal}
              interactive={true}
              concise={true}
              refreshTrigger={refreshTrigger}
              renderItem={renderProposalItem}
              options={{
                assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY'),
                topicId: selectedTopic,
                sortDescending,
                limit
              }}
            />
            <VMDelegation
              user_uuid={user_uuid}
              assembly_uuid={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
              onViewProfile={handleViewProfile}
              onDelegationChange={() => setProposalsRefreshTrigger(prev => prev + 1)}
            />
          </>
        )}
      </div>
    );
  };

  const ProposalEditPanel = ({ proposal = null, assemblyId, user_uuid, onSuccess, onNavigate, parentTopicId }) => {
    const { goBack } = usePanelNavigation();
    const [topics, setTopics] = useState([]);
    const [title, setTitle] = useState(proposal?.title || '');
    const [description, setDescription] = useState(proposal?.description || '');
    const [topicsRefreshTrigger, setTopicsRefreshTrigger] = useState(0);
    const [selectedTopic, setSelectedTopic] = useState(
      proposal ? extractTopicIdFromGSI1SK(proposal.GSI1SK) : (parentTopicId || null)
    );
    const isEdit = !!proposal;

    useEffect(() => {
      fetchTopics();
    }, [assemblyId, topicsRefreshTrigger]);

    const fetchTopics = useCallback(async () => {
      console.log("Calling fetchTopics, assemblyId = " + assemblyId);
      try {
        let jsonData = {
          command: "getTopicsForAssembly",
          assemblyId: assemblyId
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedBody = JSON.parse(data.body);
            if (parsedBody && Array.isArray(parsedBody)) {
              const noTopic = parsedBody.find(topic => topic.name === "No Topic");
              const noTopicId = noTopic ? extractSuffixFromField(noTopic.SK, 'TOPIC') : null;
              if (noTopicId && selectedTopic === null) {
                const topic = parentTopicId ? parentTopicId : noTopicId;
                setSelectedTopic(topic);
                console.log("Set selected topic to: " + topic + " parentTopicId: " + parentTopicId + " noTopic: " + noTopicId);
              } else {
                console.log("Did not set selected topic to anything.");
              }
              const sortedTopics = parsedBody.sort((a, b) =>{
                if (a.name === "No Topic") return -1;
                if (b.name === "No Topic") return 1;
                return a.name.localeCompare(b.name);
              });
              setTopics(sortedTopics);
            }
          }
        });
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    }, [user_uuid]);

    const handleSubmit = () => {
      //console.log("Handle submit, isEdit: " + isEdit + );
      if (!title.trim() || !description.trim() ) return; //|| !selectedTopic

      let jsonData = {
        command: isEdit ? "updateProposal" : "createProposal",
        assemblyId: assemblyId,
        topicId: selectedTopic,
        title: title.trim(),
        description: description.trim(),
        uuid: isEdit ? extractSuffixFromField(proposal.SK, 'PROPOSAL') : null
      };
      console.log("Submitting proposal, isEdit: " + isEdit + ", jsonData: ", jsonData);
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log(`${isEdit ? 'Updated' : 'Created'} proposal successfully`);
          if (onSuccess) { onSuccess(); }
          goBack();
        } else {
          console.log("No body present.");
          goBack();
        }
      });
    };

    const handleDelete = () => {
      if (!isEdit) return;
  
      let jsonData = {
        command: "deleteProposal",
        uuid: extractSuffixFromField(proposal.SK, 'PROPOSAL')
      };
      sendRequest(lambdaURL, jsonData)
      .then(data => {
        if (data.body) {
          console.log("Deleted proposal successfully");
          if (onSuccess) { onSuccess(); }
          goBack();
        }
      });
    };

    const handleCreateTopic = () => {
      onNavigate(
        <TopicEditPanel
          assemblyId={assemblyId}
          user_uuid={user_uuid}
          onSuccess={() => setTopicsRefreshTrigger(prev => prev + 1)}
        />
      );
    };

    //select dropdown:      value={selectedTopic}
    return (
      <div className="vm-section">
        <div className="vm-section-title">
          <h2>{isEdit ? 'Edit' : 'Create'} Proposal</h2>
        </div>
        <div className="vm-form-vertical">
          <input
            className="vm-form-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Proposal title"
          />

          <textarea
            className="vm-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Proposal description..."
            rows="6"
          />

          <div className="flex items-center gap-2">
            <label>Topic:</label>&nbsp;&nbsp;
            <select
              className="metabi-gpt-input-dropdown"
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
            >
              {topics.map(topic => (
                <option key={extractSuffixFromField(topic.SK, 'TOPIC')}
                  value={extractSuffixFromField(topic.SK, 'TOPIC')}>
                  {topic.name}
                </option>
              ))}
            </select>

            <button
              className="metabi-gpt-plus-button"
              onClick={() => handleCreateTopic() }
            >
              +
            </button>
          </div>

          <div className="vm-proposal-vote-button-container">
            <button
              className="vm-form-button"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save' : 'Create'}
            </button>
            {isEdit && (
              <button
                className="vm-form-button"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
            <button
              className="vm-form-button"
              onClick={goBack}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SlidingPanelSystem initialPanel={<AssemblyListPanel />} />
  );
};
//  <SlidingPanelSystem initialPanel={<VMHomePanel  />} />

const UnwrappedVMHome = VMHome;

/*
  <VMProfileCompletionGate>
    <UnwrappedVMHome />
  </VMProfileCompletionGate>
*/
const GatedVMHome = () => (
  <UnwrappedVMHome />
);

export { GatedVMHome as VMHome };